/**
 * Plan Activity 埋点 —— GA + OpenReplay 的**单一出口**（`emitPlanEvent`）。
 *
 * 与同域另两个文件的边界见 `dashboard/planDashboardAnalytics.ts` 顶部说明。
 * 本文件只管 Plan Activity 页，不管 Dashboard。
 */
import { trackEvent } from "@utils/tracking";
import { Track } from "@/src/lib/tracking";

const SESSION_PREFIX = "plan_activity_analytics:";

function trackPlanViewOnce(
  sessionKey: string,
  event: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (typeof window === "undefined") {
    return;
  }
  const storageKey = `${SESSION_PREFIX}${sessionKey}`;
  if (sessionStorage.getItem(storageKey)) {
    return;
  }
  sessionStorage.setItem(storageKey, "1");
  emitPlanEvent(event, params, "view"); // once-per-session 的都是曝光
}

/**
 * Plan 域埋点的**唯一出口**。
 *
 * - OpenReplay：新主通道（规范 §0 铁律）
 * - GA：`trackEvent` 暂时保留 —— plan 事件不在 §0 的 Adjust/GA 例外清单里。
 *   链路（已核对代码）：`trackEvent` → `logEventAnalytics` → Firebase `logEvent("edgen_"+name)`
 *   → **GA4，仅此一处**。摘掉只影响 GA4 看板，不涉及阿里云 SLS（那条是 logbeacon 的）。
 *   **2026-08-07 决策：GA 通道不删除，长期并行。** 本出口就是终态，不是过渡态。
 *   约束：不许新增 GA 落点；两条通道必须用同一个事件名发（规范 §0「存量 GA / GTM 落点」）。
 *
 * OR 只在这里发一次 —— 每个事件在 OpenReplay 里只能有一条记录（规范 §5）。
 */
function emitPlanEvent(
  event: string,
  params: Record<string, string | number | boolean> | undefined,
  type: "view" | "action",
): void {
  trackEvent(event, params);
  if (type === "view") {
    Track.view(event, params);
  } else {
    Track.event(event, params);
  }
}

export function trackPlanUnactivatedGoalTap(hasPlan: boolean): void {
  emitPlanEvent("plan_unactivated_goal_tap", { has_plan: hasPlan }, "action");
}

export function trackPlanSetGoalTap(): void {
  emitPlanEvent("plan_set_goal_tap", undefined, "action");
}

export function trackPlanSetGoalDone(): void {
  trackPlanViewOnce("set_goal_done", "plan_set_goal_done");
}

export function trackPlanGoalDetailView(): void {
  trackPlanViewOnce("goal_detail_view", "plan_goal_detail_view", {
    activated: false,
  });
}

export function trackPlanCalendarLockTap(): void {
  emitPlanEvent("plan_calendar_lock_tap", undefined, "action");
}

export function trackPlanDashboardUnlockTap(): void {
  emitPlanEvent("plan_dashboard_unlock_tap", undefined, "action");
}

export function trackPlanActivatedGoalChatTap(): void {
  emitPlanEvent("plan_activated_goal_chat_tap", undefined, "action");
}

// ─── Slice 5: Onboarding activation funnel events ───

/** home_state_view — Plan tab first paint */

/** checkup_start — Start a checkup from EdFlagCard */
export function trackCheckupStart(
  entry: "plan_flagged_empty" | "plan_flagged_allclear",
): void {
  emitPlanEvent("checkup_start", { entry }, "action");
}

/** activate_goal_tap — Activate goal from PlanActivityBannerSection */

/** calendar_paywall_view — Calendar lock card exposed */
export function trackCalendarPaywallView(): void {
  emitPlanEvent("calendar_paywall_view", undefined, "view");
}

/** get_full_access_tap — Get full access button on lock card */
export function trackGetFullAccessTap(): void {
  emitPlanEvent("get_full_access_tap", undefined, "action");
}

/** calendar_section_view — Calendar segment rendered */
export function trackCalendarSectionView(params: {
  today_state: "full" | "empty";
  upcoming_state: "full" | "empty";
}): void {
  emitPlanEvent("calendar_section_view", params, "view");
}
